import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

interface ImportCalculation {
  valorFOB: number;
  arancel: number;
  derechoAntidumping: number;
  tasaEstadistica: number;
  baseIVA: number;
  iva: number;
  ivaAdicional: number;
  impuestoGanancias: number;
  percepcionIB: number;
  flete: number;
  seguro: number;
  costoTransferenciaBancaria: number;
  gastosDespachante: number;
  costoFinal: number;
  costoTotalEnFOB: number;
}

interface VentaCalculation {
  costoUnitario: number;
  valorFOB: number;
  impuestosImportacion: number;
  gastosOperativosImportacion: number;
  flete: number;
  seguro: number;
  transferenciaBancaria: number;
  gastosDespachante: number;
  costoTotalDeducible: number;
  margenGanancia: number;
  precioVenta: number;
  precioVentaEnFOB: number;
  precioVentaConIVA: number;
  baseImponibleGanancias: number;
  gananciaImponible: number;
  iibbComercio: number;
  iibbIndustria: number;
  impuestoGananciasVenta: number;
  comisionVenta: number;
  gastosFijos: number;
  honorariosSocios: number;
  honorariosDeducibles: number;
  totalRecibido: number;
  gananciaNeta: number;
  margenNeto: number;
}

type TabType = 'importacion' | 'venta';

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('importacion');
  
  const [valorFOB, setValorFOB] = useState<number>(1000);
  const [tasaArancel, setTasaArancel] = useState<number>(35);
  const [tasaAntidumping, setTasaAntidumping] = useState<number>(0);
  const [tasaIVA, setTasaIVA] = useState<number>(21);
  const [tipoIVAAdicional, setTipoIVAAdicional] = useState<'10' | '20'>('10');
  const [tasaGanancias, setTasaGanancias] = useState<number>(6);
  const [tasaPercepcionIB, setTasaPercepcionIB] = useState<number>(3);
  const [flete, setFlete] = useState<number>(0);
  const [seguro, setSeguro] = useState<number>(0);
  const [costoTransferenciaBancaria, setCostoTransferenciaBancaria] = useState<number>(0);
  const [gastosDespachante, setGastosDespachante] = useState<number>(0);
  const [calculo, setCalculo] = useState<ImportCalculation | null>(null);
  
  const [margenNetoDeseado, setMargenNetoDeseado] = useState<number>(20);
  const [comisionVenta, setComisionVenta] = useState<number>(0);
  const [gastosFijos, setGastosFijos] = useState<number>(0);
  const [porcentajeHonorariosSocios, setPorcentajeHonorariosSocios] = useState<number>(25);
  const [calculoVenta, setCalculoVenta] = useState<VentaCalculation | null>(null);

  useEffect(() => {
    calcularImportacion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valorFOB, tasaArancel, tasaAntidumping, tasaIVA, tipoIVAAdicional,
      tasaGanancias, tasaPercepcionIB, flete, seguro, costoTransferenciaBancaria, gastosDespachante]);

  useEffect(() => {
    if (calculo) {
      calcularVenta();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculo, margenNetoDeseado, comisionVenta, gastosFijos, porcentajeHonorariosSocios]);

  const calcularImportacion = () => {
    const valorFOBNumerico = valorFOB || 0;

    const valorCIF = valorFOBNumerico + flete + seguro;

    const arancelCalculado = (valorCIF * tasaArancel) / 100;

    const derechoAntidumpingCalculado = (valorCIF * tasaAntidumping) / 100;

    // Tasa Estadística con topes máximos según normativa
    let tasaEstadisticaCalculada = valorFOBNumerico * 0.005;
    let maximoTasaEstadistica = 0;

    if (valorFOBNumerico <= 10000) {
      maximoTasaEstadistica = 180;
    } else if (valorFOBNumerico <= 100000) {
      maximoTasaEstadistica = 3000;
    } else if (valorFOBNumerico <= 1000000) {
      maximoTasaEstadistica = 30000;
    } else {
      maximoTasaEstadistica = 150000;
    }

    tasaEstadisticaCalculada = Math.min(tasaEstadisticaCalculada, maximoTasaEstadistica);

    const baseDerechos = valorCIF + arancelCalculado + derechoAntidumpingCalculado + tasaEstadisticaCalculada;

    const ivaCalculado = (baseDerechos * tasaIVA) / 100;

    // IVA Adicional: 10% o 20% sobre la base imponible
    const ivaAdicionalCalculado = (baseDerechos * parseFloat(tipoIVAAdicional)) / 100;

    const impuestoGananciasCalculado = (baseDerechos * tasaGanancias) / 100;

    const percepcionIBCalculada = (baseDerechos * tasaPercepcionIB) / 100;

    const costoFinal = baseDerechos + ivaCalculado + ivaAdicionalCalculado +
                      impuestoGananciasCalculado + percepcionIBCalculada +
                      costoTransferenciaBancaria + gastosDespachante;

    const costoTotalEnFOB = valorFOBNumerico > 0 ? costoFinal / valorFOBNumerico : 0;

    setCalculo({
      valorFOB: valorFOBNumerico,
      arancel: arancelCalculado,
      derechoAntidumping: derechoAntidumpingCalculado,
      tasaEstadistica: tasaEstadisticaCalculada,
      baseIVA: baseDerechos,
      iva: ivaCalculado,
      ivaAdicional: ivaAdicionalCalculado,
      impuestoGanancias: impuestoGananciasCalculado,
      percepcionIB: percepcionIBCalculada,
      flete,
      seguro,
      costoTransferenciaBancaria,
      gastosDespachante,
      costoFinal,
      costoTotalEnFOB
    });
  };

  const calcularVenta = () => {
    if (!calculo) return;

    // Deductible import costs (flete, seguro, transferencia, despachante) - operating expenses only
    const gastosOperativosImportacion = flete + seguro + costoTransferenciaBancaria + gastosDespachante;

    // Import taxes (NOT deductible for income tax): arancel, antidumping, tasa estadistica, IVA, IVA adicional, ganancias, IIBB
    const impuestosImportacion = calculo.arancel + calculo.derechoAntidumping + calculo.tasaEstadistica +
                                   calculo.iva + calculo.ivaAdicional + calculo.impuestoGanancias + calculo.percepcionIB;

    // Calculate deductible cost for income tax: FOB + Operating Expenses (NOT including import taxes)
    const costoDeducibleGanancias = valorFOB + gastosOperativosImportacion;

    // Calculate required price to achieve desired net margin
    // Working backwards from desired net margin
    const margenNetoDeseadoDecimal = margenNetoDeseado / 100;

    // Formula derivation (according to AFIP):
    // Gross Profit = Price - Cost
    // Income Tax Base = Gross Profit - Fixed Costs - Commission
    // NOTE: Import taxes (arancel, IVA, etc.) are NOT deductible for income tax
    // Only FOB + Operating Expenses (flete, seguro, transferencia, despachante) are deductible
    // Income Tax = Income Tax Base * 30%
    // Net Profit After Income Tax = Gross Profit - Fixed Costs - Commission - Income Tax
    // IIBB = Price * 4.5% (3% commerce + 1.5% industry)
    // REAL Net Profit (in pocket) = Net Profit After Income Tax - IIBB
    // Net Margin = REAL Net Profit / Price

    // This is an iterative approach to find the price that yields the desired net margin
    let precioVenta = costoDeducibleGanancias * (1 + margenNetoDeseadoDecimal * 2); // Initial estimate
    let margenNetoCalculado = 0;
    let iterations = 0;
    const maxIterations = 100;
    const tolerance = 0.0001;

    while (iterations < maxIterations && Math.abs(margenNetoCalculado - margenNetoDeseadoDecimal) > tolerance) {
      const iibbComercioCalculado = (precioVenta * 3) / 100;
      const iibbIndustriaCalculado = (precioVenta * 1.5) / 100;
      const totalIIBB = iibbComercioCalculado + iibbIndustriaCalculado;

      const comisionCalculada = (precioVenta * comisionVenta) / 100;

          // Income tax base: Gross Profit (based on deductible cost) - Fixed Costs - Commission - Socios' Fees (capped at 25%)
          // Deductible costs: FOB + Operating Expenses (flete, seguro, transferencia, despachante)
          const gananciaBrutaDeducible = precioVenta - costoDeducibleGanancias;
          
          const gananciaImponibleSinHonorarios = gananciaBrutaDeducible - gastosFijos - comisionCalculada;
          const honorariosCalculados = gananciaImponibleSinHonorarios * (porcentajeHonorariosSocios / 100);
          const topeHonorarios = gananciaImponibleSinHonorarios * 0.25;
          const honorariosDeducibles = Math.min(honorariosCalculados, topeHonorarios);
          
          const baseImponibleGanancias = gananciaBrutaDeducible - gastosFijos - comisionCalculada - honorariosDeducibles;
      
          // Income tax is calculated on the tax base, NOT including IIBB
          const impuestoGananciasVentaCalculado = Math.max(0, baseImponibleGanancias * 0.30);
      // Net profit calculation (includes import taxes as real cost)
      const margenCalculadoReal = precioVenta - calculo.costoFinal;
      const gananciaNetaDespuesGanancias = margenCalculadoReal - gastosFijos - comisionCalculada - impuestoGananciasVentaCalculado;

      // REAL net profit (what you actually keep in your pocket) - AFTER IIBB
      const gananciaNetaReal = gananciaNetaDespuesGanancias - totalIIBB;

      // Net margin is based on REAL net profit (what you keep in pocket)
      margenNetoCalculado = gananciaNetaReal / precioVenta;

      // Adjust price based on difference
      const difference = margenNetoCalculado - margenNetoDeseadoDecimal;
      precioVenta = precioVenta * (1 - difference);

      iterations++;
    }

    // Recalculate all values with final price
    const margenCalculadoReal = precioVenta - calculo.costoFinal;
    const iibbComercioCalculado = (precioVenta * 3) / 100;
    const iibbIndustriaCalculado = (precioVenta * 1.5) / 100;
    const totalIIBB = iibbComercioCalculado + iibbIndustriaCalculado;
    const comisionCalculada = (precioVenta * comisionVenta) / 100;

    // Income tax base: Gross Profit (based on deductible cost) - Fixed Costs - Commission - Socios' Fees (capped at 25%)
    // Deductible costs: FOB + Operating Expenses (flete, seguro, transferencia, despachante)
    const gananciaBrutaDeducible = precioVenta - costoDeducibleGanancias;
    
    const gananciaImponibleSinHonorariosFinal = gananciaBrutaDeducible - gastosFijos - comisionCalculada;
    const honorariosCalculadosFinal = gananciaImponibleSinHonorariosFinal * (porcentajeHonorariosSocios / 100);
    const topeHonorariosFinal = gananciaImponibleSinHonorariosFinal * 0.25;
    const honorariosDeduciblesFinal = Math.min(honorariosCalculadosFinal, topeHonorariosFinal);
    const baseImponibleGanancias = gananciaBrutaDeducible - gastosFijos - comisionCalculada - honorariosDeduciblesFinal;
    const impuestoGananciasVentaCalculado = Math.max(0, baseImponibleGanancias * 0.30);

    // Net profit calculation (includes import taxes as real cost)
    const gananciaNetaCalculada = margenCalculadoReal - gastosFijos - comisionCalculada - impuestoGananciasVentaCalculado - totalIIBB;

    // Los honorarios de socios también pagan Ganancias (30%)
    const honorariosNetosFinal = honorariosCalculadosFinal * (1 - 0.30);

    // Total received by business/socio (net profit + socios' fees after taxes)
    // This represents what actually goes into the partners' pockets
    const totalRecibidoFinal = gananciaNetaCalculada + honorariosNetosFinal;
    
    // Margen neto calculado sobre el total recibido (lo que realmente se lleva en bolsillo)
    const margenNetoCalculadoFinal = (totalRecibidoFinal / precioVenta) * 100;

    const precioVentaEnFOB = valorFOB > 0 ? precioVenta / valorFOB : 0;

    // Precio de venta + IVA (usando el mismo IVA que la importación)
    const ivaVentaCalculado = (precioVenta * tasaIVA) / 100;
    const precioVentaConIVA = precioVenta + ivaVentaCalculado;

    setCalculoVenta({
      costoUnitario: calculo.costoFinal,
      valorFOB,
      impuestosImportacion,
      gastosOperativosImportacion,
      flete,
      seguro,
      transferenciaBancaria: costoTransferenciaBancaria,
      gastosDespachante,
      costoTotalDeducible: gastosOperativosImportacion,
      margenGanancia: margenCalculadoReal,
      precioVenta,
      precioVentaEnFOB,
      precioVentaConIVA,
      baseImponibleGanancias,
      gananciaImponible: baseImponibleGanancias,
      iibbComercio: iibbComercioCalculado,
      iibbIndustria: iibbIndustriaCalculado,
      impuestoGananciasVenta: impuestoGananciasVentaCalculado,
      comisionVenta: comisionCalculada,
      gastosFijos,
      honorariosSocios: honorariosCalculadosFinal,
      honorariosDeducibles: honorariosDeduciblesFinal,
      totalRecibido: totalRecibidoFinal,
      gananciaNeta: gananciaNetaCalculada,
      margenNeto: margenNetoCalculadoFinal
    });
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  return (
    <div className="app-container">
      <div className="container py-5">
        <div className="text-center mb-5">
          <h1 className="app-title mb-3">🇦🇷 Calculadora de Importación Argentina</h1>
          <p className="text-muted">Calcula costos completos de importación y precio de venta</p>
        </div>

        <ul className="nav nav-pills nav-fill mb-4">
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'importacion' ? 'active' : ''}`}
              onClick={() => setActiveTab('importacion')}
            >
              📦 Costos de Importación
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'venta' ? 'active' : ''}`}
              onClick={() => setActiveTab('venta')}
              disabled={!calculo}
            >
              💰 Precio de Venta
            </button>
          </li>
        </ul>

        {activeTab === 'importacion' && (
          <div className="row">
            <div className="col-lg-5 mb-4">
              <div className="card shadow-sm">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">📦 Datos de Importación</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Valor FOB (USD)</label>
                    <input
                      type="number"
                      className="form-control form-control-lg"
                      value={valorFOB}
                      onChange={(e) => setValorFOB(parseFloat(e.target.value) || 0)}
                      placeholder="Ej: 1000"
                    />
                  </div>

                  <h6 className="text-primary mt-4 mb-3">📊 Impuestos Aduaneros</h6>
                  
                  <div className="mb-3">
                    <label className="form-label">
                      Arancel ({tasaArancel}%)
                    </label>
                    <input
                      type="range"
                      className="form-range"
                      min="0"
                      max="35"
                      step="0.5"
                      value={tasaArancel}
                      onChange={(e) => setTasaArancel(parseFloat(e.target.value))}
                    />
                    <div className="small text-muted">Tasa arancelaria según NCM</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Derecho Antidumping ({tasaAntidumping}%)
                    </label>
                    <input
                      type="range"
                      className="form-range"
                      min="0"
                      max="100"
                      step="0.5"
                      value={tasaAntidumping}
                      onChange={(e) => setTasaAntidumping(parseFloat(e.target.value))}
                    />
                    <div className="small text-muted">Si aplica al producto</div>
                  </div>

                  <h6 className="text-primary mt-4 mb-3">💰 Impuestos al Valor</h6>
                  
                  <div className="mb-3">
                    <label className="form-label">IVA</label>
                    <select
                      className="form-select"
                      value={tasaIVA}
                      onChange={(e) => setTasaIVA(parseFloat(e.target.value))}
                    >
                      <option value="21">21% (General)</option>
                      <option value="10.5">10.5% (Bienes de capital)</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">IVA Adicional</label>
                    <select
                      className="form-select"
                      value={tipoIVAAdicional}
                      onChange={(e) => setTipoIVAAdicional(e.target.value as '10' | '20')}
                    >
                      <option value="10">10% del IVA General</option>
                      <option value="20">20% del IVA General</option>
                    </select>
                    <div className="small text-muted">Percepción de IVA (10-20% según producto)</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Impuesto a las Ganancias ({tasaGanancias}%)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={tasaGanancias}
                      onChange={(e) => setTasaGanancias(parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                    />
                    <div className="small text-muted">Anticipo de Ganancias (6-11%)</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Ingresos Brutos ({tasaPercepcionIB}%)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={tasaPercepcionIB}
                      onChange={(e) => setTasaPercepcionIB(parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                    />
                    <div className="small text-muted">3% comercio, 1.5% industria</div>
                  </div>

                  <h6 className="text-primary mt-4 mb-3">🚢 Gastos Adicionales</h6>
                  
                  <div className="mb-3">
                    <label className="form-label">Flete (USD)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={flete}
                      onChange={(e) => setFlete(parseFloat(e.target.value) || 0)}
                      placeholder="Ej: 150"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Seguro (USD)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={seguro}
                      onChange={(e) => setSeguro(parseFloat(e.target.value) || 0)}
                      placeholder="Ej: 50"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Costo Transferencia Bancaria (USD)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={costoTransferenciaBancaria}
                      onChange={(e) => setCostoTransferenciaBancaria(parseFloat(e.target.value) || 0)}
                      placeholder="Ej: 30"
                    />
                    <div className="small text-muted">Comisión por transferencia internacional</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Gastos Despachante (USD)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={gastosDespachante}
                      onChange={(e) => setGastosDespachante(parseFloat(e.target.value) || 0)}
                      placeholder="Ej: 100"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-7">
              {calculo && (
                <>
                  <div className="card shadow-sm mb-4">
                    <div className="card-header bg-success text-white">
                      <h5 className="mb-0">✅ Resumen de Costos</h5>
                    </div>
                    <div className="card-body">
                      <div className="cost-summary">
                        <div className="cost-item">
                          <span>Valor FOB</span>
                          <span className="cost-value">{formatCurrency(calculo.valorFOB)}</span>
                        </div>
                        <div className="cost-item">
                          <span>+ Flete</span>
                          <span className="cost-value">{formatCurrency(calculo.flete)}</span>
                        </div>
                        <div className="cost-item">
                          <span>+ Seguro</span>
                          <span className="cost-value">{formatCurrency(calculo.seguro)}</span>
                        </div>
                        <div className="cost-divider"></div>
                        <div className="cost-item">
                          <span className="fw-semibold">Valor CIF</span>
                          <span className="cost-value fw-semibold">{formatCurrency(calculo.valorFOB + calculo.flete + calculo.seguro)}</span>
                        </div>
                        <div className="cost-divider"></div>
                        <div className="cost-item">
                          <span>Arancel ({tasaArancel}%)</span>
                          <span className="cost-value text-warning">{formatCurrency(calculo.arancel)}</span>
                        </div>
                        <div className="cost-item">
                          <span>Derecho Antidumping ({tasaAntidumping}%)</span>
                          <span className="cost-value text-warning">{formatCurrency(calculo.derechoAntidumping)}</span>
                        </div>
                        <div className="cost-item">
                          <span>Tasa Estadística (0.5%)</span>
                          <span className="cost-value text-warning">{formatCurrency(calculo.tasaEstadistica)}</span>
                        </div>
                        <div className="cost-divider"></div>
                        <div className="cost-item">
                          <span className="fw-semibold">Base para Impuestos</span>
                          <span className="cost-value fw-semibold">{formatCurrency(calculo.baseIVA)}</span>
                        </div>
                        <div className="cost-divider"></div>
                        <div className="cost-item">
                          <span>IVA ({tasaIVA}%)</span>
                          <span className="cost-value text-danger">{formatCurrency(calculo.iva)}</span>
                        </div>
                        <div className="cost-item">
                          <span>IVA Adicional ({tipoIVAAdicional}% del IVA)</span>
                          <span className="cost-value text-danger">{formatCurrency(calculo.ivaAdicional)}</span>
                        </div>
                        <div className="cost-item">
                          <span>Impuesto Ganancias ({tasaGanancias}%)</span>
                          <span className="cost-value text-danger">{formatCurrency(calculo.impuestoGanancias)}</span>
                        </div>
                        <div className="cost-item">
                          <span>Ingresos Brutos ({tasaPercepcionIB}%)</span>
                          <span className="cost-value text-danger">{formatCurrency(calculo.percepcionIB)}</span>
                        </div>
                        <div className="cost-item">
                          <span>Costo Transferencia Bancaria</span>
                          <span className="cost-value">{formatCurrency(calculo.costoTransferenciaBancaria)}</span>
                        </div>
                        <div className="cost-item">
                          <span>Gastos Despachante</span>
                          <span className="cost-value">{formatCurrency(calculo.gastosDespachante)}</span>
                        </div>
                        <div className="cost-divider-double"></div>
                        <div className="cost-item total">
                          <span className="fw-bold">💵 COSTO FINAL</span>
                          <span className="cost-value total fw-bold">{formatCurrency(calculo.costoFinal)}</span>
                        </div>
                        <div className="mt-3 p-3 bg-light rounded">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-semibold">Costo total en FOB:</span>
                            <span className="badge bg-primary fs-6">{calculo.costoTotalEnFOB.toFixed(2)}x</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card shadow-sm">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">📈 Distribución de Costos</h5>
                    </div>
                    <div className="card-body">
                      <div className="cost-distribution">
                        <div className="distribution-item">
                          <div className="d-flex justify-content-between mb-1">
                            <span>Valor FOB</span>
                            <span>{((calculo.valorFOB / calculo.costoFinal) * 100).toFixed(1)}%</span>
                          </div>
                          <div className="progress" style={{height: '25px'}}>
                            <div 
                              className="progress-bar bg-primary" 
                              style={{width: `${(calculo.valorFOB / calculo.costoFinal) * 100}%`}}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="distribution-item mt-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span>Arancel + Antidumping</span>
                            <span>{(((calculo.arancel + calculo.derechoAntidumping) / calculo.costoFinal) * 100).toFixed(1)}%</span>
                          </div>
                          <div className="progress" style={{height: '25px'}}>
                            <div 
                              className="progress-bar bg-warning" 
                              style={{width: `${((calculo.arancel + calculo.derechoAntidumping) / calculo.costoFinal) * 100}%`}}
                            ></div>
                          </div>
                        </div>

                        <div className="distribution-item mt-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span>Impuestos (IVA, IVA Adicional, Ganancias, IIBB)</span>
                            <span>{(((calculo.iva + calculo.ivaAdicional + calculo.impuestoGanancias + calculo.percepcionIB) / calculo.costoFinal) * 100).toFixed(1)}%</span>
                          </div>
                          <div className="progress" style={{height: '25px'}}>
                            <div 
                              className="progress-bar bg-danger" 
                              style={{width: `${((calculo.iva + calculo.ivaAdicional + calculo.impuestoGanancias + calculo.percepcionIB) / calculo.costoFinal) * 100}%`}}
                            ></div>
                          </div>
                        </div>

                        <div className="distribution-item mt-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span>Flete + Seguro + Otros</span>
                            <span>{(((calculo.flete + calculo.seguro + calculo.gastosDespachante + calculo.costoTransferenciaBancaria + calculo.tasaEstadistica) / calculo.costoFinal) * 100).toFixed(1)}%</span>
                          </div>
                          <div className="progress" style={{height: '25px'}}>
                            <div
                              className="progress-bar bg-info"
                              style={{width: `${((calculo.flete + calculo.seguro + calculo.gastosDespachante + calculo.costoTransferenciaBancaria + calculo.tasaEstadistica) / calculo.costoFinal) * 100}%`}}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'venta' && calculo && calculoVenta && (
          <div className="row">
            <div className="col-lg-5 mb-4">
              <div className="card shadow-sm">
                <div className="card-header bg-success text-white">
                  <h5 className="mb-0">💰 Parámetros de Venta</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Margen Neto Deseado ({margenNetoDeseado}%)
                    </label>
                    <input
                      type="range"
                      className="form-range"
                      min="0"
                      max="100"
                      step="1"
                      value={margenNetoDeseado}
                      onChange={(e) => setMargenNetoDeseado(parseFloat(e.target.value))}
                    />
                    <div className="small text-muted">Porcentaje de ganancia neta deseado sobre el precio de venta</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Comisión de Venta ({comisionVenta}%)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={comisionVenta}
                      onChange={(e) => setComisionVenta(parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                    />
                    <div className="small text-muted">Comisión por venta (vendedor, marketplace, etc.)</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Gastos Fijos (USD)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={gastosFijos}
                      onChange={(e) => setGastosFijos(parseFloat(e.target.value) || 0)}
                      placeholder="Ej: 50"
                    />
                    <div className="small text-muted">Gastos fijos por unidad (packaging, logística, etc.)</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Honorarios Socios (%)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={porcentajeHonorariosSocios}
                      onChange={(e) => setPorcentajeHonorariosSocios(parseFloat(e.target.value) || 0)}
                      placeholder="Ej: 25"
                      min="0"
                      max="100"
                    />
                    <div className="small text-muted">Porcentaje de ganancia imponible (deducible hasta 25%)</div>
                  </div>

                  <div className="mt-4 p-3 bg-light rounded">
                    <h6 className="fw-bold mb-3">📋 Resumen del Costo</h6>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Costo de Importación:</span>
                      <span className="fw-bold">{formatCurrency(calculoVenta ? calculoVenta.costoUnitario : calculo.costoFinal)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-7">
              <div className="card shadow-sm mb-4">
                <div className="card-header bg-success text-white">
                  <h5 className="mb-0">💵 Cálculo del Precio de Venta</h5>
                </div>
                <div className="card-body">
                  <div className="cost-summary">
                    <div className="cost-item">
                      <span className="fw-bold">📦 COSTO DE IMPORTACIÓN TOTAL</span>
                      <span className="cost-value fw-bold">{formatCurrency(calculoVenta.costoUnitario)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-3">• Valor FOB</span>
                      <span className="cost-value text-muted">{formatCurrency(calculoVenta.valorFOB)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-3">• Gastos Operativos</span>
                      <span className="cost-value text-muted">{formatCurrency(calculoVenta.gastosOperativosImportacion)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-3">• Impuestos Importación</span>
                      <span className="cost-value text-muted">{formatCurrency(calculoVenta.impuestosImportacion)}</span>
                    </div>
                    <div className="cost-divider"></div>
                    <div className="cost-item">
                      <span>+ Margen Bruto ({((calculoVenta.margenGanancia / calculoVenta.costoUnitario) * 100).toFixed(1)}%)</span>
                      <span className="cost-value text-success">{formatCurrency(calculoVenta.margenGanancia)}</span>
                    </div>
                    <div className="cost-divider"></div>
                    <div className="cost-item">
                      <span className="fw-semibold">Precio de Venta (sin IVA)</span>
                      <span className="cost-value fw-semibold">{formatCurrency(calculoVenta.precioVenta)}</span>
                    </div>
                    <div className="cost-divider-double"></div>

                    <div className="cost-item">
                      <span className="fw-bold text-primary">📊 CÁLCULO DE GANANCIA IMPOSIBLE (AFIP)</span>
                      <span></span>
                    </div>
                    <div className="cost-item">
                      <span>Ingreso Bruto (Precio Venta)</span>
                      <span className="cost-value">{formatCurrency(calculoVenta.precioVenta)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-success ps-3">📦 COSTOS DEDUCIBLES</span>
                      <span className="cost-value text-success">-{formatCurrency(calculoVenta.valorFOB + calculoVenta.gastosOperativosImportacion)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-5">• Valor FOB</span>
                      <span className="cost-value text-muted">-{formatCurrency(calculoVenta.valorFOB)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-5">• Flete</span>
                      <span className="cost-value text-muted">-{formatCurrency(calculoVenta.flete)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-5">• Seguro</span>
                      <span className="cost-value text-muted">-{formatCurrency(calculoVenta.seguro)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-5">• Transferencia Bancaria</span>
                      <span className="cost-value text-muted">-{formatCurrency(calculoVenta.transferenciaBancaria)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-5">• Despachante</span>
                      <span className="cost-value text-muted">-{formatCurrency(calculoVenta.gastosDespachante)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-danger ps-3">⚠️ Impuestos Importación (NO deducibles)</span>
                      <span className="cost-value text-danger">{formatCurrency(calculoVenta.impuestosImportacion)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-3">• Arancel</span>
                      <span className="cost-value text-muted">{formatCurrency(calculoVenta.impuestosImportacion - calculoVenta.impuestosImportacion * 0.5)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-3">• IVA + Adicional</span>
                      <span className="cost-value text-muted">{formatCurrency(calculoVenta.impuestosImportacion * 0.3)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-3">• Ganancias + IIBB Import.</span>
                      <span className="cost-value text-muted">{formatCurrency(calculoVenta.impuestosImportacion * 0.2)}</span>
                    </div>
                    <div className="cost-divider"></div>
                    <div className="cost-item">
                      <span className="fw-bold">Ganancia Bruta (para AFIP)</span>
                      <span className="cost-value fw-bold">{formatCurrency(calculoVenta.precioVenta - calculoVenta.valorFOB - calculoVenta.gastosOperativosImportacion)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-3">- Gastos Fijos Deducibles</span>
                      <span className="cost-value text-muted">-{formatCurrency(calculoVenta.gastosFijos)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-3">- Comisión Venta</span>
                      <span className="cost-value text-muted">-{formatCurrency(calculoVenta.comisionVenta)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-3">- Honorarios Socios (25% max)</span>
                      <span className="cost-value text-muted">-{formatCurrency(calculoVenta.honorariosSocios)}</span>
                    </div>
                    <div className="cost-item">
                      <span className="text-muted ps-5">• Ganancias sobre Honorarios (30%)</span>
                      <span className="cost-value text-danger">-{formatCurrency(calculoVenta.honorariosSocios * 0.30)}</span>
                    </div>
                    <div className="cost-divider"></div>
                    <div className="cost-item">
                      <span className="fw-bold">Base Imponible Ganancias</span>
                      <span className="cost-value fw-bold">{formatCurrency(calculoVenta.baseImponibleGanancias)}</span>
                    </div>
                    <div className="cost-divider"></div>
                    <div className="cost-item">
                      <span className="fw-bold">Ganancia Imponible (para AFIP)</span>
                      <span className="cost-value fw-bold text-primary">{formatCurrency(calculoVenta.gananciaImponible)}</span>
                    </div>
                    <div className="cost-divider"></div>
                    <div className="cost-item">
                      <span>- Impuesto a las Ganancias (30%)</span>
                      <span className="cost-value text-danger">-{formatCurrency(calculoVenta.impuestoGananciasVenta)}</span>
                    </div>
                    <div className="cost-item">
                      <span>- IIBB (4.5%: 3% Comercio + 1.5% Industria)</span>
                      <span className="cost-value text-danger">-{formatCurrency(calculoVenta.iibbComercio + calculoVenta.iibbIndustria)}</span>
                    </div>
                    <div className="cost-divider-double"></div>
                    <div className="cost-item">
                      <span className="fw-bold">💵 GANANCIA NETA REAL (después Ganancias + IIBB)</span>
                      <span className="cost-value fw-bold text-success">{formatCurrency(calculoVenta.gananciaNeta)}</span>
                    </div>
                    <div className="cost-item">
                      <span>+ Honorarios Socios (netos, después 30% Ganancias)</span>
                      <span className="cost-value text-info">{formatCurrency(calculoVenta.honorariosSocios * 0.70)}</span>
                    </div>
                    <div className="cost-divider"></div>
                    <div className="cost-item">
                      <span className="fw-bold">💰 TOTAL RECIBIDO (Neto + Honorarios)</span>
                      <span className="cost-value fw-bold text-success">{formatCurrency(calculoVenta.totalRecibido)}</span>
                    </div>
                    <div className="cost-divider-double"></div>
                    <div className="cost-item total">
                      <span className="fw-bold">💰 PRECIO DE VENTA FINAL</span>
                      <span className="cost-value total fw-bold">{formatCurrency(calculoVenta.precioVenta)}</span>
                    </div>
                    <div className="cost-item">
                      <span>+ IVA ({tasaIVA}%)</span>
                      <span className="cost-value text-danger">{formatCurrency(calculoVenta.precioVentaConIVA - calculoVenta.precioVenta)}</span>
                    </div>
                    <div className="cost-divider"></div>
                    <div className="cost-item total">
                      <span className="fw-bold">💵 PRECIO FINAL + IVA</span>
                      <span className="cost-value total fw-bold text-primary">{formatCurrency(calculoVenta.precioVentaConIVA)}</span>
                    </div>
                    <div className="mt-3 p-3 bg-light rounded">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-semibold">Impuesto Ganancias (30% sobre base imponible):</span>
                        <span className="text-danger">{formatCurrency(calculoVenta.impuestoGananciasVenta)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-semibold">IIBB (3% Comercio + 1.5% Industria):</span>
                        <span className="text-danger">{formatCurrency(calculoVenta.iibbComercio + calculoVenta.iibbIndustria)}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-bold">Ganancia Neta Real:</span>
                        <span className={`badge ${calculoVenta.gananciaNeta > 0 ? 'bg-success' : 'bg-danger'} fs-6`}>
                          {formatCurrency(calculoVenta.gananciaNeta)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <span className="fw-bold">Honorarios Socios (netos):</span>
                        <span className={`badge ${calculoVenta.honorariosSocios > 0 ? 'bg-info' : 'bg-secondary'} fs-6`}>
                          {formatCurrency(calculoVenta.honorariosSocios * 0.70)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <span className="fw-bold">Total Recibido:</span>
                        <span className={`badge ${calculoVenta.totalRecibido > 0 ? 'bg-success' : 'bg-danger'} fs-6`}>
                          {formatCurrency(calculoVenta.totalRecibido)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <span className="fw-semibold">Margen Neto (sobre Total Recibido):</span>
                        <span className={`badge ${calculoVenta.margenNeto > 0 ? 'bg-primary' : 'bg-danger'} fs-6`}>
                          {calculoVenta.margenNeto.toFixed(2)}%
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <span className="fw-bold">Precio de venta en FOB:</span>
                        <span className="badge bg-info fs-6">
                          {calculoVenta.precioVentaEnFOB.toFixed(2)}x
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card shadow-sm">
                <div className="card-header bg-info text-white">
                  <h5 className="mb-0">📊 Distribución del Precio</h5>
                </div>
                <div className="card-body">
                  <div className="cost-distribution">
                    <div className="distribution-item">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Costo de Importación</span>
                        <span>{((calculoVenta.costoUnitario / calculoVenta.precioVenta) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="progress" style={{height: '25px'}}>
                        <div 
                          className="progress-bar bg-primary" 
                          style={{width: `${(calculoVenta.costoUnitario / calculoVenta.precioVenta) * 100}%`}}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="distribution-item mt-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Impuestos Venta (IIBB + Ganancias)</span>
                        <span>{(((calculoVenta.iibbComercio + calculoVenta.iibbIndustria + calculoVenta.impuestoGananciasVenta) / calculoVenta.precioVenta) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="progress" style={{height: '25px'}}>
                        <div 
                          className="progress-bar bg-danger" 
                          style={{width: `${((calculoVenta.iibbComercio + calculoVenta.iibbIndustria + calculoVenta.impuestoGananciasVenta) / calculoVenta.precioVenta) * 100}%`}}
                        ></div>
                      </div>
                    </div>

                    <div className="distribution-item mt-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Comisión + Gastos Fijos</span>
                        <span>{(((calculoVenta.comisionVenta + calculoVenta.gastosFijos) / calculoVenta.precioVenta) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="progress" style={{height: '25px'}}>
                        <div 
                          className="progress-bar bg-warning" 
                          style={{width: `${((calculoVenta.comisionVenta + calculoVenta.gastosFijos) / calculoVenta.precioVenta) * 100}%`}}
                        ></div>
                      </div>
                    </div>

                    <div className="distribution-item mt-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Ganancia Neta Real</span>
                        <span>{((calculoVenta.gananciaNeta / calculoVenta.precioVenta) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="progress" style={{height: '25px'}}>
                        <div 
                          className={`progress-bar ${calculoVenta.gananciaNeta > 0 ? 'bg-success' : 'bg-danger'}`} 
                          style={{width: `${Math.abs(calculoVenta.gananciaNeta / calculoVenta.precioVenta) * 100}%`}}
                        ></div>
                      </div>
                    </div>

                    <div className="distribution-item mt-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Honorarios Socios (Deducibles)</span>
                        <span>{((calculoVenta.honorariosDeducibles / calculoVenta.precioVenta) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="progress" style={{height: '25px'}}>
                        <div 
                          className="progress-bar bg-info" 
                          style={{width: `${(calculoVenta.honorariosDeducibles / calculoVenta.precioVenta) * 100}%`}}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;